// FR-1 tuning state machine. Consumes AnalyzedFrame, emits TuningFrameResult.
// State transitions (spec §4.1.1), with the 250 ms "locked" threshold measured
// in wall-clock time (independent of analysis frame rate).
//
// 增强:音量阈值过滤(低于阈值的帧视为静音,不更新指针)+ EMA 平滑(指针不乱跳)。

import type { AnalyzedFrame } from '../audio/engine';
import type { TuningFrameResult, StringType } from '../types';
import { nearestString } from '../audio/freq';
import { openStringFrequencies } from '../data/pitches';

type Status = TuningFrameResult['status'];

// RMS 百分比 -> RMS 的换算基准(MAX_RMS 对应 100%)。
export const MAX_RMS = 0.2;
export function percentToRms(percent: number): number {
  return (percent / 100) * MAX_RMS;
}

export class Tuner {
  private A4: number;
  private manualString: StringType | null = null;
  private status: Status = 'capturing';

  // 可调参数
  private volumeThreshold: number; // RMS,低于此值视为静音
  private smoothing: number; // 0-100,越大越平滑

  private confStreak = 0; // capturing -> analyzing (2 frames conf>0.4 & locked)
  private stableStreak = 0; // analyzing -> stable (5 frames conf>0.7 & |cents|<15)
  private lockedSince: number | null = null; // stable -> locked (250 ms)
  private lastValid: number | null = null; // any -> capturing (1 s no valid pitch)

  // EMA 平滑状态
  private smoothedCents: number | null = null; // null = 无有效值
  private lastActiveString: StringType | null = null;

  onResult: ((r: TuningFrameResult) => void) | null = null;
  onLocked: ((string: StringType) => void) | null = null;

  constructor(A4 = 440, volumeThresholdPercent = 12, smoothingPercent = 60) {
    this.A4 = A4;
    this.volumeThreshold = percentToRms(volumeThresholdPercent);
    this.smoothing = smoothingPercent;
  }

  setA4(A4: number): void {
    this.A4 = A4;
  }

  setVolumeThreshold(percent: number): void {
    this.volumeThreshold = percentToRms(percent);
  }

  setSmoothing(percent: number): void {
    this.smoothing = percent;
  }

  setManualString(s: StringType | null): void {
    this.manualString = s;
  }

  reset(): void {
    this.status = 'capturing';
    this.confStreak = 0;
    this.stableStreak = 0;
    this.lockedSince = null;
    this.lastValid = null;
    this.smoothedCents = null;
    this.lastActiveString = null;
  }

  // EMA alpha: smoothing 0 -> 1(无平滑), 100 -> 0.1(很平滑)
  private alpha(): number {
    return Math.max(0.1, 1 - (this.smoothing / 100) * 0.9);
  }

  process(frame: AnalyzedFrame): TuningFrameResult {
    const now = frame.time;
    const targets = openStringFrequencies(this.A4);

    // 音量阈值过滤:低于阈值的帧视为静音。
    const loudEnough = frame.rms >= this.volumeThreshold;
    const pitchPresent = frame.freq > 0 && frame.confidence >= 0.3;
    const validPitch = loudEnough && pitchPresent;

    const nearest = frame.freq > 0 ? nearestString(frame.freq, targets) : { index: -1, cents: 0 };
    const activeString: StringType | null = this.manualString ?? (nearest.index >= 0 ? (nearest.index as StringType) : null);

    // 计算 cents(原始)
    let rawCents = 0;
    if (validPitch && activeString !== null && frame.freq > 0) {
      rawCents = 1200 * Math.log2(frame.freq / targets[activeString]);
    }

    // EMA 平滑:换弦或丢失信号时重置,避免跨弦插值导致指针漂移。
    if (!validPitch || activeString !== this.lastActiveString) {
      this.smoothedCents = validPitch ? rawCents : null;
      this.lastActiveString = validPitch ? activeString : this.lastActiveString;
    } else if (this.smoothedCents === null) {
      this.smoothedCents = rawCents;
    } else {
      this.smoothedCents = this.alpha() * rawCents + (1 - this.alpha()) * this.smoothedCents;
    }

    const clamped = this.smoothedCents === null ? 0 : Math.max(-50, Math.min(50, this.smoothedCents));
    const conf = frame.confidence;
    const direction: TuningFrameResult['direction'] =
      this.smoothedCents === null ? 'flat' : Math.abs(clamped) < 5 ? 'in-tune' : clamped < 0 ? 'flat' : 'sharp';

    if (validPitch) this.lastValid = now;

    // any -> capturing: 1 s with no valid pitch.
    if (this.lastValid !== null && now - this.lastValid > 1000 && this.status !== 'capturing') {
      this.transition('capturing');
    }

    if (this.status === 'capturing') {
      if (validPitch && conf > 0.4 && activeString !== null) {
        this.confStreak++;
        if (this.confStreak >= 2) this.transition('analyzing');
      } else {
        this.confStreak = 0;
      }
    }

    if (this.status === 'analyzing') {
      if (validPitch && conf > 0.7 && Math.abs(clamped) < 15) {
        this.stableStreak++;
        if (this.stableStreak >= 5) this.transition('stable');
      } else {
        this.stableStreak = 0;
      }
      if (Math.abs(clamped) > 10 || conf < 0.5 || !validPitch) {
        this.lockedSince = null;
      }
    }

    if (this.status === 'stable') {
      if (validPitch && conf > 0.8 && Math.abs(clamped) < 5) {
        if (this.lockedSince === null) this.lockedSince = now;
        if (now - this.lockedSince >= 250) {
          this.transition('locked');
          if (activeString !== null) this.onLocked?.(activeString);
        }
      } else {
        this.lockedSince = null;
        if (Math.abs(clamped) > 10 || conf < 0.5 || !validPitch) this.transition('analyzing');
      }
    }

    if (this.status === 'locked') {
      if (Math.abs(clamped) > 10 || conf < 0.5 || !validPitch) {
        this.transition('analyzing');
        this.lockedSince = null;
      }
    }

    const result: TuningFrameResult = {
      timestamp: now,
      activeString,
      centsDeviation: Math.round(clamped),
      confidence: conf,
      direction,
      status: this.status,
    };
    this.onResult?.(result);
    return result;
  }

  private transition(next: Status): void {
    this.status = next;
    if (next === 'capturing') {
      this.confStreak = 0;
      this.stableStreak = 0;
      this.lockedSince = null;
    }
    if (next === 'analyzing') this.stableStreak = 0;
  }
}
